import { useState } from 'react';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { classNames } from '@/lib/utils';
import { monthRange, yearRange } from '@/lib/analyticsDates';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Hay reservas desde 2019 y confirmadas de meses futuros, de ahí el +1.
const ANIO_MIN = 2019;

type Periodo =
  | { tipo: 'anio'; anio: number }
  | { tipo: 'mes'; anio: number; mes: number }
  | { tipo: 'custom'; anio: number };

/**
 * Deriva el periodo SIEMPRE de from/to en lugar de guardar estado propio: así
 * el control no puede mentir cuando el rango se cambia desde las fechas del
 * panel desplegado (pasa a «Personalizado» solo).
 */
function derivarPeriodo(from: string, to: string): Periodo {
  const f = new Date(from + 'T00:00:00');
  // Un input date vaciado emite '': sin este guard el año del select sería NaN.
  if (!from || !to || Number.isNaN(f.getTime())) {
    return { tipo: 'custom', anio: new Date().getFullYear() };
  }
  const anio = f.getFullYear();
  const rAnio = yearRange(anio);
  if (from === rAnio.from && to === rAnio.to) return { tipo: 'anio', anio };
  const mes = f.getMonth();
  const rMes = monthRange(anio, mes);
  if (from === rMes.from && to === rMes.to) return { tipo: 'mes', anio, mes };
  return { tipo: 'custom', anio };
}

/**
 * Sustituye a la vez la rejilla de 12 meses y los presets rápidos («Este mes»,
 * «Este año»…): todos eran la misma elección de mes y año contada dos veces.
 * Cada cambio emite el rango completo y la página recarga al recibirlo.
 */
export function SelectorPeriodo({ from, to, onChange }: {
  from: string;
  to: string;
  onChange: (r: { from: string; to: string }) => void;
}) {
  const anioMax = new Date().getFullYear() + 1;
  const periodo = derivarPeriodo(from, to);
  const anio = Math.min(Math.max(periodo.anio, ANIO_MIN), anioMax);
  const anios: number[] = [];
  for (let y = ANIO_MIN; y <= anioMax; y++) anios.push(y);

  const emitir = (tipo: 'anio' | 'mes', a: number, m: number) => {
    onChange(tipo === 'anio' ? yearRange(a) : monthRange(a, m));
  };

  // Con mes concreto las flechas cruzan el año; con «Año completo» saltan de
  // año en año. Con rango personalizado no hay paso obvio: se deshabilitan.
  const mover = (delta: number) => {
    if (periodo.tipo === 'mes') {
      const d = new Date(anio, periodo.mes + delta, 1);
      emitir('mes', d.getFullYear(), d.getMonth());
    } else if (periodo.tipo === 'anio') {
      emitir('anio', anio + delta, 0);
    }
  };

  const atrasDeshabilitado =
    periodo.tipo === 'custom' ||
    (periodo.tipo === 'mes' ? anio === ANIO_MIN && periodo.mes === 0 : anio <= ANIO_MIN);
  const adelanteDeshabilitado =
    periodo.tipo === 'custom' ||
    (periodo.tipo === 'mes' ? anio === anioMax && periodo.mes === 11 : anio >= anioMax);

  const claseFlecha =
    'min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg border border-sereno-dark/15 bg-white text-sereno-dark/60 hover:border-sereno-dark/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button onClick={() => mover(-1)} disabled={atrasDeshabilitado} className={claseFlecha} aria-label="Periodo anterior">
        <ChevronLeft size={16} />
      </button>
      <select
        value={periodo.tipo === 'custom' ? 'custom' : periodo.tipo === 'anio' ? 'anio' : String(periodo.mes)}
        onChange={(e) => {
          if (e.target.value === 'anio') emitir('anio', anio, 0);
          else emitir('mes', anio, Number(e.target.value));
        }}
        className="input min-h-11 w-auto max-w-full"
        aria-label="Mes"
      >
        {periodo.tipo === 'custom' && (
          <option value="custom" disabled>
            Personalizado
          </option>
        )}
        <option value="anio">Año completo</option>
        {MESES.map((nombre, i) => (
          <option key={i} value={String(i)}>
            {nombre}
          </option>
        ))}
      </select>
      <select
        value={String(anio)}
        onChange={(e) => {
          const a = Number(e.target.value);
          if (periodo.tipo === 'mes') emitir('mes', a, periodo.mes);
          else emitir('anio', a, 0);
        }}
        className="input min-h-11 w-auto max-w-full"
        aria-label="Año"
      >
        {anios.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
      <button onClick={() => mover(1)} disabled={adelanteDeshabilitado} className={claseFlecha} aria-label="Periodo siguiente">
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

/**
 * Tarjeta de filtros plegable: la barra (selector de periodo) siempre visible
 * y el resto detrás del botón «Filtros». Sin persistencia a propósito: quien
 * lo abre es para un ajuste puntual y al volver quiere la pantalla limpia.
 */
export default function PanelFiltros({ barra, activos, abiertoInicial = false, children }: {
  barra: React.ReactNode;
  activos: number;
  abiertoInicial?: boolean;
  children: React.ReactNode;
}) {
  const [abierto, setAbierto] = useState(abiertoInicial);

  return (
    <div className="card mb-6">
      <div className="flex flex-wrap items-center gap-3">
        {barra}
        <button
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className={classNames(
            'ml-auto min-h-11 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border bg-white transition-colors',
            abierto
              ? 'text-sereno-dark border-sereno-dark dark:border-gray-100'
              : 'text-sereno-dark/70 border-sereno-dark/15 hover:border-sereno-dark/40'
          )}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {activos > 0 && <span className="badge bg-sereno-dark text-white an-active">{activos}</span>}
        </button>
      </div>

      {/* El panel empuja el contenido (no flota): abrirlo no tapa los datos. */}
      {abierto && <div className="mt-4 pt-4 border-t border-sereno-dark/10">{children}</div>}
    </div>
  );
}
